// ============================================================================
// frontend/src/components/tenant/ThemeCustomizer.tsx
// ============================================================================

import React from 'react';
import { TenantFormData } from '@/types/tenant';
import { FONT_PRESETS } from '@/constants/themes';

interface ThemeCustomizerProps {
  formData: TenantFormData;
  setFormData: (data: TenantFormData) => void;
}

export const ThemeCustomizer: React.FC<ThemeCustomizerProps> = ({
  formData,
  setFormData
}) => {
  return (
    <div className="space-y-6 p-6 bg-muted/30 rounded-lg border-2 border-dashed">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Vous pouvez personnaliser tous les aspects du thème</span>
      </div>

      {/* Mode clair */}
      <div className="space-y-4">
        <h4 className="font-medium text-sm">Mode clair</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur principale</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.primary_color}
                onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur secondaire</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.secondary_color}
                onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur d'accent</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.accent_color}
                onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.accent_color}
                onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur destructive</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.destructive_color}
                onChange={(e) => setFormData({ ...formData, destructive_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.destructive_color}
                onChange={(e) => setFormData({ ...formData, destructive_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mode sombre */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm">Mode sombre</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Couleur principale</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.dark_primary_color}
                onChange={(e) => setFormData({ ...formData, dark_primary_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.dark_primary_color}
                onChange={(e) => setFormData({ ...formData, dark_primary_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Fond</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.dark_background_color}
                onChange={(e) => setFormData({ ...formData, dark_background_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.dark_background_color}
                onChange={(e) => setFormData({ ...formData, dark_background_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Texte</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.dark_foreground_color}
                onChange={(e) => setFormData({ ...formData, dark_foreground_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.dark_foreground_color}
                onChange={(e) => setFormData({ ...formData, dark_foreground_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Cartes</label>
            <div className="flex gap-2 items-center">
              <input
                type="color"
                value={formData.dark_card_color}
                onChange={(e) => setFormData({ ...formData, dark_card_color: e.target.value })}
                className="h-10 w-16 rounded border border-input cursor-pointer"
              />
              <input
                type="text"
                value={formData.dark_card_color}
                onChange={(e) => setFormData({ ...formData, dark_card_color: e.target.value })}
                className="flex-1 h-10 rounded border border-input bg-background px-3 text-sm font-mono"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Typographie */}
      <div className="space-y-4 pt-4 border-t">
        <h4 className="font-medium text-sm">Typographie</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Police principale</label>
            <select
              value={formData.font_family}
              onChange={(e) => setFormData({ ...formData, font_family: e.target.value })}
              className="flex h-10 w-full rounded border border-input bg-background px-3 py-2 text-sm"
            >
              {FONT_PRESETS.map(font => (
                <option key={font.name} value={font.value}>
                  {font.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Arrondi des bordures</label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="2"
                step="0.05"
                value={formData.border_radius}
                onChange={(e) => setFormData({ ...formData, border_radius: e.target.value })}
                className="flex-1"
              />
              <input
                type="text"
                value={`${formData.border_radius}rem`}
                readOnly
                className="w-24 h-10 rounded border border-input bg-muted px-3 text-sm text-center font-mono"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};